import { apiGet, apiPost } from '../../../shared/api/client';
import { endpoints } from '../../../shared/config/endpoints';
import { buildSubscriptionDataResponse } from './mergePipeline';
import type { SubscriptionDataResponse } from './types';
import {
  normalizeSubscriptionListResponse,
  normalizeSubscriptionNewResponse,
  normalizeSubscriptionSubDataResponse,
} from './normalizers';

const MY_SUBS_ENDPOINT = (endpoints as { mySubscriptions?: string }).mySubscriptions ?? 'customer_subscriptions';

interface MySubscriptionsListResponse {
  success?: boolean;
  data?: {
    subscriptions?: Array<{
      type_name?: string;
      type_data?: Array<{ id?: number; payment_status?: string; [key: string]: unknown }>;
    }>;
  };
}

export async function fetchMySubscriptionsList(customer_id: number): Promise<MySubscriptionsListResponse> {
  return apiGet<MySubscriptionsListResponse>(MY_SUBS_ENDPOINT, { customer_id });
}

export function getPendingSubscriptionFromResponse(res: MySubscriptionsListResponse): any | null {
  const list = res?.data?.subscriptions;
  if (!Array.isArray(list)) {return null;}
  for (const subscription of list) {
    const name = subscription.type_name ?? '';
    if (name.indexOf('Pending Subscriptions') === -1 && name.indexOf('Upcoming Subscriptions') === -1) {continue;}
    const type_data = subscription.type_data ?? [];
    for (const sub of type_data) {
      if (sub.payment_status === 'pending') {return sub;}
    }
  }
  return null;
}

/**
 * Abandon a **pending** (unpaid) checkout row before creating a new purchase, or remove from My Subscriptions.
 * Legacy Ionic used `customer_subscriptions/cancel` (POST), not `DELETE customer_subscriptions/:id` (often 404).
 */
export async function deletePendingSubscription(customer_id: number, customer_subscription_id: number | string): Promise<void> {
  await apiPost(endpoints.subscriptionCancel, {
    customer_id,
    customer_subscription_id: Number(customer_subscription_id),
  });
}

async function fetchRawSubscriptionSubData(customer_id: number): Promise<unknown> {
  return apiGet<unknown>(endpoints.subData, { customer_id });
}

async function fetchRawSubscriptionNew(customer_id: number): Promise<unknown> {
  return apiGet<unknown>(endpoints.mySubscriptionsnew, { customer_id });
}

async function fetchRawSubscriptionList(customer_id: number): Promise<unknown> {
  return apiGet<unknown>(endpoints.mySubscriptions, { customer_id });
}

/** 3 parallel GETs → normalizers → merge pipeline. */
export async function fetchUpgradeItems(
  customer_id: number,
  customer_subscription_id: number | string,
): Promise<{ success?: boolean; data?: { items?: unknown[] }; message?: string }> {
  return apiGet(endpoints.upgradeItems, { customer_id, customer_subscription_id });
}

/** Checkout: POST `customer_subscriptions/upgrade` → charge URL. */
export async function postUpgradeSubscription(payload: object): Promise<{
  success?: boolean;
  data?: { charge_url?: string };
  message?: string;
}> {
  return apiPost(endpoints.subscriptionUpgrade, payload);
}

export async function fetchSubscriptionMerged(customer_id: number): Promise<SubscriptionDataResponse> {
  const [raw_new, raw_flat, raw_list] = await Promise.all([
    fetchRawSubscriptionNew(customer_id),
    fetchRawSubscriptionSubData(customer_id),
    fetchRawSubscriptionList(customer_id),
  ]);

  return buildSubscriptionDataResponse({
    customer_id,
    sub_new: normalizeSubscriptionNewResponse(raw_new),
    sub_data: normalizeSubscriptionSubDataResponse(raw_flat),
    sub_list: normalizeSubscriptionListResponse(raw_list),
  });
}
